import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { authenticatedTestKitValidationSchema } from '../../../validations/common';
import { RequestOptions } from '../../../types/httpRequests';
import BorderedButton from '../../common/buttons/BorderedButton';
import PrimaryBlueButton from '../../common/buttons/PrimaryBlueButton';
import useBackendApi from '../../../hooks/useBackendApi';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';
import { TestKitActivationLastSteps } from '../../../types/user';
import WarningToMailSampleBack from '../WarningToMailSampleBack';
import { CustomDialog } from '../../common/CustomDialog';
import TestKitsValidatedOver18Warning from '../TestKitsValidatedOver18Warning';
import DisableActivation from '../DisableActivation';

const backendURL = process.env.REACT_APP_BACKEND_URL;

interface AuthTestKitActivationFormProps {
  setShopifyOrderId: (orderId: string) => void;
  setTestKitStep: (step: string) => void;
  setFinalStepsData: (finalStepsData: TestKitActivationLastSteps) => void;
  activationCodeValue: string;
  disableTestKitActivation: boolean;
}

export default function AuthTestKitActivationForm({
  setShopifyOrderId,
  setTestKitStep,
  setFinalStepsData,
  activationCodeValue,
  disableTestKitActivation,
}: AuthTestKitActivationFormProps) {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiFetchRequest } = useBackendApi();
  const { addAlertMessages } = useThemeContext();

  const [errorMsgDescription, setErrorMsgDescription] = useState('');
  const [errorMsgTitle, setErrorMsgTitle] = useState('');
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  const handleSubmit = async (values: { activationCode: string; testSubjectName: string; email: string }) => {
    setIsSubmitting(true);
    const encodedActivationCode = encodeURIComponent(values.activationCode);
    const testKitActivationUrl = `${backendURL}/test_kits/${encodedActivationCode}/activate`;

    const apiRequestOptions: RequestOptions = {
      method: 'PUT',
      endpoint: testKitActivationUrl,
    };

    // I'm very tempted to extract this conditional requests into a separate function
    // and use something like tankstack/react-query
    // but probably not worth it for now
    // TODO: if we have more conditions to meet let's jump into the refactoring
    // and use that library which would help to allow the conditional
    // fetching of data and is way simpler to use and maintain
    try {
      const response = await apiFetchRequest(apiRequestOptions);

      if (response.status !== 200) {
        setErrorMsgDescription(response.body.error_description);
        setErrorMsgTitle(response.body.error_title);
        setIsErrorDialogOpen(true);
        return;
      }

      const shopifyOrderId = response.body.s_order_id;
      setShopifyOrderId(response.body.s_order_id);
      const finalStepsData = {
        shopifyOrderId: shopifyOrderId,
        activationCode: values.activationCode,
      };
      setFinalStepsData(finalStepsData);
      setTestKitStep('activationSubmitSuccess');
    } catch (err) {
      addAlertMessages({
        message: 'An error happened during the test kit activation',
        severityType: 'error',
      });
      console.log(err, 'err');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      activationCode: activationCodeValue,
      testSubjectName: '',
      email: '',
    },
    validationSchema: authenticatedTestKitValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleCloseCustomDialog = () => {
    setIsErrorDialogOpen(false);
    setErrorMsgDescription('');
    setErrorMsgTitle('');
  };

  const handleActivateTestSubmit = () => {
    formik.handleSubmit();
  };

  return (
    <Box sx={{ marginBottom: '100px' }}>
      <React.Fragment>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', smMd700: 'column', smMd800: 'row', md: 'row' },
          }}
        >
          <Box
            component="img"
            sx={{
              height: '100%',
              width: { sm: '100%', smMd800: '40%' },
              marginRight: '29px',
              display: { xs: 'none', smMd700: 'none', smMd800: 'block' },
              marginTop: { xs: 0, sm: '24px', md: '24px' },
            }}
            alt="Activate test kit"
            src="/images/test-kit-activation.png"
          />
          <Box sx={{ width: { xs: '100%', md: '60%' } }}>
            {disableTestKitActivation ? (
              <DisableActivation />
            ) : (
              <>
                <Box sx={{ display: 'flex', margin: 'auto', mt: 2, pd: 2, flexDirection: 'column', maxWidth: 600 }}>
                  <Typography variant="h4">Activate test kit</Typography>
                  <Box
                    component="img"
                    sx={{
                      height: '100%',
                      width: '100%',
                      marginRight: '29px',
                      marginBottom: 2,
                      display: { xs: 'block', smMd800: 'none' },
                      marginTop: '15px',
                    }}
                    alt="Activate test kit"
                    src="/images/test-kit-activation-mobile.png"
                  />
                  <Typography variant="body2" sx={{ mt: 1 }} gutterBottom>
                    Please enter your activation code.
                  </Typography>
                  <TextField
                    label="Activation code"
                    sx={{ mb: 2 }}
                    {...formik.getFieldProps('activationCode')}
                    placeholder="e.g. AB11-123-654-789"
                    fullWidth
                    required
                    error={formik.touched.activationCode && Boolean(formik.errors.activationCode)}
                    helperText={formik.touched.activationCode && formik.errors.activationCode}
                  />
                  <Typography variant="body2" gutterBottom>
                    After taking the test, please remember to write the name and date of birth of the test subject on
                    the label provided before returning the kit to the lab.
                  </Typography>
                </Box>
                <WarningToMailSampleBack />
                <TestKitsValidatedOver18Warning />
                <Box
                  sx={{
                    display: 'flex',
                    margin: 'auto',
                    marginBottom: '20px',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: { xs: 'center', md: 'space-between' },
                    maxWidth: { xs: '100%', sm: '600px' },
                  }}
                >
                  <PrimaryBlueButton
                    isLoading={isSubmitting}
                    onClick={handleActivateTestSubmit}
                    buttonText="Activate"
                    sxProps={{
                      mb: { xs: 1, md: 0 },
                    }}
                    additionalProps={{
                      disabled: !formik.isValid || isSubmitting,
                    }}
                  />
                  <BorderedButton onClick={() => navigate('/')} buttonText="Cancel activation" />
                </Box>
              </>
            )}
          </Box>
          <CustomDialog
            title={errorMsgTitle}
            errorMessage={errorMsgDescription}
            isOpen={isErrorDialogOpen}
            onClose={handleCloseCustomDialog}
          />
        </Box>
      </React.Fragment>
    </Box>
  );
}
